import { ArrowDownward } from "@mui/icons-material";
import CachedIcon from "@mui/icons-material/Cached";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpIcon from "@mui/icons-material/Help";
import { Collapse, TextField } from "@mui/material";
import { styled, useTheme } from "@mui/system";
import { debounce } from "lodash";
import React from "react";
import { NumericFormat, NumericFormatProps } from "react-number-format";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { trackEvent } from "src/Amplitude";
import ContainedButton from "src/components/common/ContainedButton";
import { formatPrice } from "src/components/portfolio/helpers";
import BalanceTooltip from "src/components/wallets/header/BalanceTooltip";
import PresentIcon from "src/icons/PresentIcon";
import api from "src/store/features/api";
import { CreateTradeDto, TradeSummaryDto } from "src/store/features/api/generated";
import { setFetchRecentTrades, useSwapSlice } from "src/store/features/swap.slice";
import { useUserSlice } from "src/store/features/user.slice";
import usePermission from "src/utils/hooks/usePermission";
import { TradeStyles } from "../styles/Trade.styles";
import useTradeErrors from "./hooks/useTradeErrors";
import useTradeInputs from "./hooks/useTradeInputs";
import useTradeRefLogic from "./hooks/useTradeRefLogic";
import useTradeSummary from "./hooks/useTradeSummary";
import useTradeUI from "./hooks/useTradeUI";
import TradeCoinSelectionButton from "./TradeCoinSelectionButton";
import TradeConfirmationModal from "./TradeConfirmationModal";
import TradeLockedUserModal from "./TradeLockedUserModal";
import { setPendingVerificationModal } from "src/store/features/modals.slice";
import useVerificationNavigation from "src/components/common/useVerificationNavigation";

export const Wrapper = styled("div")(({ theme }) => ({
  width: "100%",
  height: "100%",
  borderRadius: "30px",
  background: theme.palette.primary.main,
  boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
  padding: "24px",
  display: "flex",
  flexDirection: "column",
  gap: "14px",
}));

export const HeaderWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontWeight: "bold",
  fontSize: "16px",
  color: "#fff",
}));

interface ClearIconWrapperProps {
  isLoading: boolean;
}
export const ClearIconWrapper = styled("div")<ClearIconWrapperProps>(({ isLoading }) => ({
  display: "flex",

  // ANIMATION
  animation: isLoading ? "bezier-spin 1.5s cubic-bezier(0.42, 0, 0.58, 1) infinite" : "",

  "@keyframes bezier-spin": {
    from: { transform: "rotate(0deg)" },
    to: { transform: "rotate(360deg)" },
  },
}));

export const InputsWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  alignItems: "center",
  position: "relative",
}));

export const InputWrapper = styled("div")(({ theme }) => ({
  border: "1px solid #e0e0e0",
  backgroundColor: theme.palette.success.light,
  color: theme.palette.common.black,
  borderRadius: "20px",
  justifyContent: "center",
  alignItems: "center",
  display: "flex",
  minHeight: "84px",
  width: "100%",
}));

export const CustomInputField = styled(TextField)(({ theme }) => ({
  width: "100%",
  fontSize: "24px",
  padding: "0px 24px",

  [theme.breakpoints.down("sm")]: {
    padding: "0px 12px",
  },
}));

interface SwapIconWrapperProps {
  clicked: boolean;
  disable: boolean;
}
export const SwapIconWrapper = styled("div")<SwapIconWrapperProps>(
  ({ theme, clicked, disable }) => ({
    position: "absolute",
    top: 65,
    color: "#002035",
    background: theme.palette.secondary.contrastText,
    borderRadius: "10px",
    width: "48px",
    height: "48px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: disable ? "default" : "pointer",
    pointerEvents: disable ? "none" : "auto",
    transition: "transform .5s",
    boxShadow: "0px 2px 2px 0px rgba(42, 95, 129, 0.30)",

    "& svg:first-of-type": {
      fontSize: "2rem",
    },

    "&:hover": {
      background: "#E5E7E8",
      transform: "rotate(-180deg)",
      boxShadow: "0px -2px 2px 0px rgba(42, 95, 129, 0.30)",
    },

    "&:active": {
      background: theme.palette.secondary.contrastText,
    },

    [theme.breakpoints.down(600)]: {
      top: 69,
      transform: "scale(.8)",
      // ANIMATION
      "&:hover": {
        transform: "rotate(-180deg) scale(.8)",
      },

      "&:active": {
        background: theme.palette.secondary.contrastText,
      },
    },
  }),
);

export const CollapsibleErrorWrapper = styled("div")(() => ({
  height: "fit-content",
  display: "flex",
}));

export const SummaryAndButtonWrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "24px",
  alignSelf: "stretch",
  height: "100%",
}));

export const CalculationsWrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  alignSelf: "stretch",
}));

interface CalcTextProps {
  weight?: number;
}

export const CalcText = styled("div")<CalcTextProps>(({ weight }) => ({
  alignSelf: "stretch",
  color: "#FFF",
  fontFamily: "Roobert",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: weight ?? 600,
  lineHeight: "19px",
  letterSpacing: "-0.32px",
}));

interface ArrowWrapperProps {
  clicked: boolean;
}
const ArrowWrapper = styled("div")<ArrowWrapperProps>(({ clicked }) => ({
  display: "flex",
  position: "relative",
  left: 5,

  animation: clicked ? "upwards 0.5s forwards" : "downwards 0.5s forwards",
  // ANIMATION
  "@keyframes upwards": {
    " 0%": { transform: "rotate(0deg);" },
    "100% ": { transform: "rotate(-180deg)" },
  },

  "@keyframes downwards": {
    " 0%": { transform: "rotate(-180deg);" },
    "100% ": { transform: "rotate(0deg)" },
  },
}));

interface expectedOutputWrapperProps {
  open: boolean;
}
export const ExpectedOutputWrapper = styled("div")<expectedOutputWrapperProps>(({ open }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "8px",
  alignSelf: "stretch",
  paddingTop: "14px",
  cursor: "pointer",
  paddingBottom: open ? "8px" : "0px",

  // smooths out padding change
  transition: "all 0.5s",
}));

export const CollapseContent = styled("div")(() => ({
  color: "#FFF",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
}));

export const CollapseDivider = styled("div")(() => ({
  borderBottom: "1px solid #FFF",
  width: "100%",
  marginBottom: "14px",
  paddingBottom: "8px",
}));

export const HelpIconSpan = styled("span")(() => ({
  fontSize: "14px",
  paddingRight: "4px",
  display: "inline-flex",
}));

export const FeeWrapper = styled("div")(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "8px",
  alignSelf: "stretch",
}));

export const TotalWrapper = styled("div")(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "8px",
  alignSelf: "stretch",
}));

export const ButtonText = styled("div")(() => ({
  fontSize: "18px",
}));

export const PromotionalPlacard = styled("div")(() => ({
  display: "flex",
  padding: "4px 8px",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  borderRadius: "4px",
  background: "#F8F9F9",
  color: "#002035",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "16px",
}));

const HELP_TOOLTIP =
  "Your trade has one all-inclusive broker fee covering costs associated with slippage, foreign exchange, and labour involved. A further breakdown will be provided in your invoice.";

interface TradeCardProps {
  toggleTradeSuccess: React.DispatchWithoutAction;
}

const TradeCard = (props: TradeCardProps) => {
  const { toggleTradeSuccess } = props;
  const classes = TradeStyles();
  const location = useLocation();
  const {
    is2FAEnabled,
    details: { customerId },
    defaultCurrency,
  } = useUserSlice();
  const { knownSide } = useSwapSlice();
  const { code: userBaseCurrency } = defaultCurrency;
  const dispatch = useDispatch();
  const theme = useTheme();
  const { handleVerification } = useVerificationNavigation();

  // trade summary
  const [tradeSummary, setTradeSummary] = React.useState<TradeSummaryDto | null>(null);

  // user id ref
  const currencyUserID = React.useRef<string>(customerId);

  // APIs
  const { data: user, refetch: refetchUserAPI } = api.useGetUserDatav2Query();
  const [postTradeSummary, summaryResp] = api.useGetTradeSummaryMutation();
  const [createTrade, createTradeResp] = api.useCreateCtMutation();
  const [getPartnershipInfo] = api.useLazyGetReferalPartnerDetailsQuery();

  // ipnut ref logic - hook
  const {
    ref,
    handleSellInputKeyDown,
    handleBuyInputKeyDown,
    handleInputFocus,
    handleInputBlur,
    resetInputRefs,
  } = useTradeRefLogic();

  // user input data - hook
  const { sellInput, handleSellInputChange, buyInput, handleBuyInputChange, resetInputs } =
    useTradeInputs();

  // summary logic - hook
  const { summary, handleSummaryChange, resetSummary } = useTradeSummary({ buyInput });

  // trade UI logic - hook
  const {
    clearButton,
    submitButton,
    isSummaryOpen,
    hasInputsSwapped,
    isCreateCTLoading,
    isTradeConfirmationOpen,
    userLocked,
    promotionalPlacardMessage,
    resetTradeUI,
    handleTradeInputSwap,
    handleSubmitButtonChange,
    handleToggleSummaryCollapse,
    handleToggleTradeConfirmation,
    handleUserLockedChange,
    handlePromotionalPlacardMessage,
  } = useTradeUI({
    summaryIsLoading: summaryResp.isLoading,
    ctIsLoading: createTradeResp.isLoading,
  });

  // error handling logic - hook
  const { error, hasError, changeErrorMessage, handleCustomError, handleErrorFromSummaryResponse } =
    useTradeErrors({ sellInput, buyInput, ref, handleSubmitButtonChange });

  /**
   * Resets all values to their default state.
   *
   * This function resets various states to their initial default states.
   */
  const resetToDefault = () => {
    resetInputs();
    resetSummary();
    resetInputRefs();
    changeErrorMessage("reset");
    resetTradeUI();
  };

  /**
   * useEffect to reset the state to default if the customer ID changes.
   *
   * This effect monitors the `customerId` dependency and compares it with the `currencyUserID`.
   * If the `customerId` changes and does not match `currencyUserID.current`, the state is reset to default.
   * Because we also have a feature to populate the buy field with a currency code from the URL,
   * we check if the URL contains "buy" before resetting the state to default.
   * this is so it doesnt overwrite the currency code in the buy field in the TradeCoinSelectionButton component.
   */
  React.useEffect(() => {
    if (location.search.includes("code")) return;
    if (currencyUserID.current !== customerId) {
      currencyUserID.current = customerId;
      resetToDefault();
    }
  }, [customerId, location]);

  /**
   * Handles the calculation of the trade summary by making a query to the backend.
   *
   * This function prepares a TradeSummaryDto object based on user inputs,
   * ensuring that properties with a value of 0 are removed to determine which
   * side (buy or sell) is known. It then attempts to post the trade summary data
   * to the server.
   */
  const handleTradeSummaryCalculation = async (): Promise<void> => {
    const tradeSummaryDto: TradeSummaryDto = {
      sell: { units: Number(sellInput.units), code: sellInput.code },
      buy: { units: Number(buyInput.units), code: buyInput.code },
    };

    if (knownSide === "sell") {
      tradeSummaryDto.buy.units = undefined;
    }

    if (knownSide === "buy") {
      tradeSummaryDto.sell.units = undefined;
    }
    // // Remove properties with units === 0
    // // this determines which side is known.
    if (tradeSummaryDto.sell.units === 0) tradeSummaryDto.sell.units = undefined;
    if (tradeSummaryDto.buy.units === 0) tradeSummaryDto.buy.units = undefined;

    // in some rare cases, if both inputs are 0, set the input element units that is focused to use value as a backup number.
    // usually occurs when the user switches currency and the input field is filled
    if (!tradeSummaryDto.sell.units && !tradeSummaryDto.buy.units) {
      if (knownSide === "sell") {
        // user inputted in sell field meaning sell field is the known side.
        tradeSummaryDto.sell.units = Number(sellInput.value);
      }
      if (knownSide === "buy") {
        // user inputted in buy field meaning buy field is the known side.
        tradeSummaryDto.buy.units = Number(buyInput.value);
      }
    }

    if (
      (!tradeSummaryDto.buy?.units || tradeSummaryDto.buy.units === 0) &&
      (!tradeSummaryDto.sell?.units || tradeSummaryDto.sell.units === 0)
    ) {
      return;
    }
    setDidFetch(true);

    postTradeSummary({ tradeSummaryDto })
      .unwrap()
      .then((summary): void => {
        handleSummaryChange(summary);
        setTradeSummary(tradeSummaryDto);
        // error message can come from a successfull response
        if (handleErrorFromSummaryResponse(summary)) return;
        // handle setting the summary / expected output values
      })
      .catch((error): void => {
        trackEvent("Error User View Trade Summary", { rejected: summary.error }, "Trade");
        if (error.message.includes("minimum transaction")) {
          changeErrorMessage("belowMinTrade");
        } else {
          handleCustomError(`${error.message}. Press clear and try again`);
        }
      })
      .finally((): void => {
        setDidFetch(false);
      });
  };

  /**
   * State to track whether the summary has been fetched.
   * Avoid being able to 'calculate' the summary while calculating the summary is already is progress.
   */
  const [didFetch, setDidFetch] = React.useState<boolean>(false);

  /**
   * Determines if the trade summary can be fetched based on various conditions.
   * If you have HARD conditions that must be met before fetching the summary, add them here.
   * @returns {boolean} True if the summary can be fetched, otherwise false.
   */
  const canFetchSummary = React.useMemo((): boolean => {
    // if sell and buy input is 0, return false
    if (sellInput.value === "0" && buyInput.value === "0") return false;

    // if sell or buy input is "Select", return false
    if (sellInput.code === "Select" || buyInput.code === "Select") return false;

    // if the summary is being fetched, return false
    if (clearButton.isLoading) return false;

    // If there is an error, return false
    if (hasError) return false;

    // If the summary was JUST fetched for the first time.
    // avoids from summary being fetched again as all other conditions are not met.
    // return false
    if (didFetch) return false;

    return true;
  }, [sellInput, buyInput, clearButton, hasError]);

  /**
   * Debounced function to handle trade summary calculation.
   * Delay is set to 1500ms (1.5 seconds).
   */
  const debouncedCalculation = debounce(handleTradeSummaryCalculation, 1500);

  /**
   * useEffect to automatically trigger the debounced calculation if the summary can be fetched.
   * Includes a fallback to ensure the calculation is triggered if debounce fails.
   */
  React.useEffect(() => {
    let fallbackTimeout: NodeJS.Timeout;

    // If the summary can be fetched, perform the debounced calculation
    if (canFetchSummary) {
      let isCalculating = true;

      const calculationPromise = debouncedCalculation();
      if (calculationPromise) {
        calculationPromise.then(() => {
          isCalculating = false;
          clearTimeout(fallbackTimeout);
        });
      }

      // Set a fallback to ensure the calculation is triggered if debounce fails
      fallbackTimeout = setTimeout(() => {
        if (isCalculating && !didFetch) {
          handleTradeSummaryCalculation();
        }
      }, 2500); // Fallback delay slightly longer than debounce delay
    }

    // Clean-up (cancel debounce and fallback) on unmount
    return (): void => {
      debouncedCalculation.cancel(); // Cancel debounce on unmount
      clearTimeout(fallbackTimeout); // Clear fallback timeout
    };
  }, [canFetchSummary, didFetch]);

  /**
   * Handles the click event of the place order button by toggling the trade confirmation modal.
   */
  const handlePlaceOrderButton = (): void => {
    if (user?.isLocked || user?.isBypassLock) {
      handleUserLockedChange({ isOpen: true });
      return;
    }
    // if sell or buy input is 0, return
    if (sellInput.value === "0" || buyInput.value === "0") return;

    // if sell or buy input is "Select", return
    if (sellInput.code === "Select" || buyInput.code === "Select") return;

    // if the summary is being fetched, return
    if (clearButton.isLoading) return;

    // If there is an error, return
    if (hasError) return;

    handleToggleTradeConfirmation();
  };

  /**
   * Handles creating CT from order. This function:
   * 1. Toggles the trade confirmation modal closing it.
   * 2. Creates a DTO for the trade based on current input values.
   * 3. Removes properties with units equal to 0 to determine which side of the trade is known.
   * 4. Calls the API to create the trade.
   * 5. Resets the form to its default state and toggles the trade success modal if the trade creation is successful.
   * 6. Handles any errors that occur during the trade creation process.
   */
  const handleOnTradeCofirm = (disclosurePayload?: {
    disclosureRequestDateTime: string;
    disclosureAcceptanceDateTime: string;
    timezone?: string;
  }): void => {
    if (!tradeSummary) return;

    const timezone =
      typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC";
    const createTradeDto: CreateTradeDto = {
      ...tradeSummary,
      datetime: new Date().toLocaleString(),
      ...(disclosurePayload && {
        disclosureRequestDateTime: disclosurePayload.disclosureRequestDateTime,
        disclosureAcceptanceDateTime: disclosurePayload.disclosureAcceptanceDateTime,
        timezone: disclosurePayload.timezone ?? timezone,
      }),
    };

    trackEvent("Placing Order", { orderPayload: createTradeDto }, "Trade");
    // Call create trade API here, then reset and toggleTradeSuccess
    createTrade({ createTradeDto })
      .unwrap()
      .then((): void => {
        trackEvent("Order Placed", { orderPayload: createTradeDto }, "Trade");
        dispatch(setFetchRecentTrades());
        refetchUserAPI();
        resetToDefault();
        toggleTradeSuccess();
      })
      .catch((error): void => {
        trackEvent("Error Placing Order", { orderPayload: createTradeDto }, "Trade");

        handleCustomError(`${error.message}. Press clear and try again`);
      })
      .finally(() => {
        handleToggleTradeConfirmation();
      });
  };

  /**
   * Generates a tooltip message for promotional fees based on partnership referral.
   */
  React.useEffect(() => {
    const getPromotionalFeeTooltip = async (): Promise<void> => {
      const feePercent: number = summary.expectedOutput.fee;
      if (!summary.partnershipCode) {
        handlePromotionalPlacardMessage(
          `A promotional fee of ${feePercent}% has been applied via your referral partner`,
        );
      }

      try {
        const resp = await getPartnershipInfo({
          referrer: summary.partnershipCode.toLowerCase(),
        }).unwrap();
        handlePromotionalPlacardMessage(
          `A promotional fee of ${feePercent}% has been applied via the referral partner ${resp.name}`,
        );
      } catch {
        handlePromotionalPlacardMessage(
          `A promotional fee of ${feePercent}% has been applied via your referral partner`,
        );
      }
    };

    getPromotionalFeeTooltip();
  }, [summary.partnershipCode]);

  return (
    <Wrapper data-testid="trade-card">
      {/* Header */}
      <HeaderWrapper>
        Swap
        <ContainedButton
          id="clear-button"
          color={theme.palette.mode === "light" ? "navy" : "dark"}
          borderRadius="20px"
          height="22px"
          width={clearButton.width}
          sx={{
            boxShadow: "none",
            border: "1px solid #FFF",
            cursor: clearButton.isLoading ? "default" : "pointer",
            pointerEvents: clearButton.isLoading ? "none" : "auto",
            transition: "all 0.2s",
            "&:hover": {
              color: theme.palette.mode === "light" ? "#4E7086" : "#FFFFFF",
              background: theme.palette.mode === "light" ? "#002035" : "#121212",
              border: theme.palette.mode === "light" ? "1px solid #4E7086" : "1px solid #333333",
            },
          }}
          endIcon={
            <ClearIconWrapper isLoading={clearButton.isLoading} data-testid="clear-button">
              <CachedIcon fontSize="small" sx={{ transform: "scaleX(-1)" }} />
            </ClearIconWrapper>
          }
          onClick={resetToDefault}
          data-testid="clear-button">
          {clearButton.text}
        </ContainedButton>
      </HeaderWrapper>

      {/* Inputs */}
      <InputsWrapper>
        {/* Input 1 */}
        <InputWrapper>
          <CustomInputField
            id="sellInput"
            inputRef={ref.sellInputRef}
            variant="standard"
            value={sellInput.value}
            onChange={handleSellInputChange}
            onKeyDown={handleSellInputKeyDown}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            disabled={clearButton.isLoading}
            InputProps={{
              inputComponent: NumericFormatCustom as any,
              disableUnderline: true,
              classes: {
                input: classes.inputStyles,
              },
              inputProps: {
                min: 0,
                max: 1000000,
                color: "#fff",
              },
              endAdornment: (
                <TradeCoinSelectionButton
                  code={sellInput.code}
                  logoURL={sellInput.logoURL}
                  maxTotal={sellInput.maxTotal}
                  action="sell"
                />
              ),
            }}
          />
        </InputWrapper>

        {/* Swap icon */}
        <SwapIconWrapper
          clicked={hasInputsSwapped}
          disable={clearButton.isLoading}
          onClick={handleTradeInputSwap}>
          <ArrowDownward />
        </SwapIconWrapper>

        {/* Input 2 */}
        <InputWrapper>
          <CustomInputField
            id="buyInput"
            inputRef={ref.buyInputRef}
            variant="standard"
            value={buyInput.value}
            onChange={handleBuyInputChange}
            onKeyDown={handleBuyInputKeyDown}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            disabled={clearButton.isLoading}
            InputProps={{
              inputComponent: NumericFormatCustom as any,
              disableUnderline: true,
              classes: {
                input: classes.inputStyles,
              },
              inputProps: {
                min: 0,
                max: 1000000,
                color: "#fff",
              },
              endAdornment: (
                <TradeCoinSelectionButton
                  code={buyInput.code}
                  logoURL={buyInput.logoURL}
                  maxTotal={buyInput.maxTotal}
                  action="buy"
                />
              ),
            }}
          />
        </InputWrapper>
      </InputsWrapper>

      {/* Error */}
      <CollapsibleErrorWrapper>
        <Collapse in={hasError} orientation="vertical" sx={{ width: "100%" }}>
          {error}
        </Collapse>
      </CollapsibleErrorWrapper>

      {/* Summary and Place order button */}
      <SummaryAndButtonWrapper>
        <CalculationsWrapper>
          {/* Conversion */}
          <CalcText id="conversion-rate">{summary.conversion}</CalcText>

          {/* Expected output */}
          <ExpectedOutputWrapper onClick={handleToggleSummaryCollapse} open={isSummaryOpen}>
            <CalcText>Expected Output</CalcText>
            <div style={{ display: "flex" }}>
              <CalcText style={{ alignSelf: "center" }}>{summary.expectedOutput.header}</CalcText>
              <ArrowWrapper clicked={isSummaryOpen}>
                <ExpandMoreIcon sx={{ color: "#FFF", display: "flex", alignItems: "flex-start" }} />
              </ArrowWrapper>
            </div>
          </ExpectedOutputWrapper>

          {/* Container for collapsible calculations */}
          {/* Broker fee and Expected output */}
          <CollapseDivider>
            <Collapse in={isSummaryOpen} orientation="vertical" sx={{ width: "100%" }}>
              <CollapseContent>
                <FeeWrapper>
                  <CalcText weight={400}>Broker Fee</CalcText>
                  <CalcText weight={400}>
                    <HelpIconSpan>
                      <BalanceTooltip message={HELP_TOOLTIP} contrast>
                        <HelpIcon fontSize="inherit" sx={{ color: "#FFF" }} />
                      </BalanceTooltip>
                    </HelpIconSpan>
                    {summary.expectedOutput.fee}%
                  </CalcText>
                </FeeWrapper>
                <FeeWrapper>
                  <CalcText weight={400}>Value</CalcText>
                  <CalcText weight={400}>
                    {formatPrice(Number(summary.expectedOutput.value), userBaseCurrency)}
                  </CalcText>
                </FeeWrapper>
              </CollapseContent>
            </Collapse>
          </CollapseDivider>

          {/* Total calculation */}
          <TotalWrapper>
            <CalcText>Total</CalcText>
            <CalcText>{formatPrice(Number(summary.total), userBaseCurrency)}</CalcText>
          </TotalWrapper>
        </CalculationsWrapper>
        {summary.isPromotional && (
          <BalanceTooltip
            id="promotional-placard-tooltip"
            message={promotionalPlacardMessage}
            contrast>
            <PromotionalPlacard>
              <PresentIcon />
              Promotional fee applied
            </PromotionalPlacard>
          </BalanceTooltip>
        )}

        {/* Place order button */}
        <ContainedButton
          id="submit-button"
          color={submitButton.color as "salmon" | "navy" | "neutral"}
          borderRadius="8px"
          height="54px"
          disabled={submitButton.disabled || hasError}
          onClick={() => handleVerification(handlePlaceOrderButton)}>
          <ButtonText>{submitButton.text}</ButtonText>
        </ContainedButton>
      </SummaryAndButtonWrapper>

      {/* Trade confirmation modal */}
      {/* edit order or confirm order */}
      {/* and */}
      {/* Warn/Block customers that DO NOT have Two Factor Authentication enabled */}
      {/* and */}
      {/* present loading bar after clicking confirm order button */}
      <TradeConfirmationModal
        open={isTradeConfirmationOpen}
        isLoading={isCreateCTLoading}
        sellUnits={sellInput.value}
        sellCode={sellInput.code}
        buyUnits={buyInput.value}
        buyCode={buyInput.code}
        isTwoFAEnabled={is2FAEnabled}
        onClose={handleToggleTradeConfirmation}
        onConfirm={handleOnTradeCofirm}
      />

      {/* Trade locked user modal */}
      {/* Prohibits user from progressing the order if their user is locked */}
      <TradeLockedUserModal
        open={userLocked.isOpen}
        onClose={() => handleUserLockedChange({ isOpen: false })}
      />
    </Wrapper>
  );
};

export default TradeCard;

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const NumericFormatCustom = React.forwardRef<NumericFormatProps, CustomProps>(
  function NumericFormatCustom(props, ref) {
    const { onChange, ...other } = props;

    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.value,
            },
          });
        }}
        thousandSeparator
        // This allows enabling or disabling leading zeros in the input field.
        allowLeadingZeros={false}
        // If set to false, negative numbers will not be allowed
        allowNegative={false}
        // Limits the number of digits after the decimal point.
        // decimalScale={2}
      />
    );
  },
);
